---
title: Live probes
lede: The agent watches one line of a running project, or of the page it opened, without editing the code or restarting anything.
order: 23
part: Check the result
---

A live probe lets the session's agent see the values a line of code holds while the program runs. The program carries on; nothing is edited or restarted. Every value captured is recorded under the session, so you can read what the agent saw.

Probes are part of [Agent MCP](/agent-preview) and are on wherever **Let the agent drive Preview** is on.

### What a probe can reach

- A project Airlock runs in the session.
- A process the agent started itself inside the micro-VM.
- The page open in the agent's browser.

Nothing outside the session. On a session placed on this computer, only the projects Airlock started can be probed.

### Two kinds of probe

| Kind | Captures | Default budget |
|---|---|---|
| Log | The expressions the agent named, every time the line runs | 200 hits (up to 1,000) |
| Snapshot | The locals and the stack, once, pausing only that moment | 1 hit |

A probe can carry a condition, so only the runs where it holds count. It takes itself out once its budget is used. The agent can also evaluate one expression in a running Node process or in the page, without pausing it.

### Which languages

| Runtime | Needs | Notes |
|---|---|---|
| Node and the page | **Attach to what runs** | TypeScript lines are mapped through source maps |
| Python and Go | **Also start Java, Python and Go under a debugger** | Expressions are evaluated in the program |
| Java | **Also start Java, Python and Go under a debugger** | Reads variable and field paths such as `order.customer.id`, nothing else |
| Rust | **Also start Java, Python and Go under a debugger** | The session image gains lldb, which attaches to the program; the project's start does not change |

### Turning it on or off

**Settings › All agents › Live probes**, with a workspace override under **Workspace settings › Agent**. It appears only while **Let the agent drive Preview** is on.

| Answer | What it does |
|---|---|
| **Off** | No probes |
| **Attach to what runs — Node and the page** | The default. Costs nothing until a probe is set |
| **Also start Java, Python and Go under a debugger** | The JVM starts with a debug agent, Python inside debugpy, and Go without optimisations; the session image gains the debuggers. Each project picks it up the next time it starts, and runs somewhat slower |

### Reading what it caught

Each probe is a row in the dock's **Agent MCP** tab, with its count of hits. Select it to see the file, line and expression, its condition, where it actually landed, its hits out of its budget, and each run's values, newest first.

| State | Means |
|---|---|
| **Live** | Set, and capturing |
| **Waiting** | Its code has not loaded yet |
| **Done** | It used its budget and took itself out |
| **Removed** | The agent took it out |
| **Detached** | Its process went away |
| **Not set** | It could not be set; the row says why |

The latest 50 runs are drawn in the row. Earlier ones are in **Projects & logs**, marked `probe`.

### Limits

| Limit | Value |
|---|---|
| Live probes per session | 20 |
| Expressions per probe | 8 |
| Hits per second | 50; past that the probe is sampled and says how many it skipped |
| One captured value | 1,000 characters |
| Locals / stack frames in a snapshot | 40 / 12 |
